import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import "./TemplateListMenu.css";

const formatPrice = (value) => `$${Number(value || 0)}`;

const getStoreName = (store) =>
  store?.name || store?.Name || store?.store_name || store?.StoreName || "菜單";

const getTableLabel = (tableNo) => {
  if (!tableNo) return "菜單";
  return tableNo === "takeout" ? "外帶 · 菜單" : `${tableNo} 號桌 · 菜單`;
};

function ProductImage({ product, className = "", size = "sm" }) {
  const imageSrc = typeof product?.image === "string" ? product.image.trim() : product?.image;
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageSrc]);

  if (imageSrc && !imageFailed) {
    return (
      <img
        className={`tlm-pic tlm-pic-img ${className}`}
        src={imageSrc}
        alt={product.name}
        loading="lazy"
        onError={() => setImageFailed(true)}
      />
    );
  }

  const glyph = product?.name ? [...product.name][0] : "圖";
  const name = product?.name || "";
  const shouldShowName = size !== "sm" && name;

  return (
    <div
      className={`tlm-pic tlm-pic-placeholder tlm-pic-${size} ${className}`}
      aria-hidden="true"
    >
      <div className="tlm-pic-scene" />
      <div className="tlm-pic-watermark">{glyph}</div>
      <div className="tlm-pic-tag">{formatPrice(product?.price)}</div>
      {shouldShowName ? <div className="tlm-pic-name">{name}</div> : null}
    </div>
  );
}

function TopBar({ store, tableNo, cartCount, openCart }) {
  return (
    <div className="tlm-topbar">
      <div className="tlm-topbar-copy">
        <div className="tlm-table">{getTableLabel(tableNo)}</div>
        <div className="tlm-store-name">{getStoreName(store)}</div>
      </div>
      <button className="tlm-cart-dot" type="button" onClick={openCart} aria-label="開啟購物車">
        {cartCount || "·"}
      </button>
    </div>
  );
}

function StoreHero({ storeId, store }) {
  const [bannerFailed, setBannerFailed] = useState(false);
  const bannerSrc = useMemo(
    () => `https://storage.googleapis.com/imao_store/${storeId}/BANNER.png?v=${Date.now()}`,
    [storeId]
  );

  useEffect(() => {
    setBannerFailed(false);
  }, [storeId]);

  if (!storeId || bannerFailed) {
    return null;
  }

  return (
    <div className="tlm-hero">
      <img
        src={bannerSrc}
        alt={`${getStoreName(store)} banner`}
        loading="eager"
        onError={() => setBannerFailed(true)}
      />
    </div>
  );
}

function SectionTitle({ title, count }) {
  return (
    <div className="tlm-sec-title">
      <span className="tlm-sec-heading">{title}</span>
      <span className="tlm-sec-count">共 {String(count).padStart(2, "0")} 項</span>
    </div>
  );
}

function CategoryRail({ categories, active, onPick }) {
  return (
    <nav className="tlm-rail" aria-label="商品分類">
      {categories.map((category, index) => (
        <button
          key={category.id}
          className={`tlm-rail-item ${active === category.id ? "active" : ""}`}
          type="button"
          title={category.label}
          onClick={() => onPick(category.id)}
        >
          <span>{category.label.slice(0, 2)}</span>
          <span className="tlm-rail-sub">{String(index + 1).padStart(2, "0")}</span>
        </button>
      ))}
    </nav>
  );
}

function InlineProductDetail({
  product,
  selectedData,
  isEditing,
  onCancel,
  onOptionChange,
  onQuantityChange,
  onAdd,
  calculateSubtotal,
  canAdd,
}) {
  const quantity = selectedData?.quantity || 1;

  return (
    <div className="tlm-inline-detail" onClick={(event) => event.stopPropagation()}>
      <div className="tlm-inline-desc">{product.description || " "}</div>
      {product.groups?.map((group) => {
        const effectiveMax = group.min_choose > 0 ? 1 : group.max_choose;
        const isSingle = effectiveMax === 1;
        return (
          <fieldset className="tlm-inline-option-group" key={group.name}>
            <legend className="tlm-inline-group-label">
              <span>{group.name}</span>
              {group.min_choose > 0 ? (
                <span className="tlm-required">必選 {group.min_choose} 項</span>
              ) : !isSingle ? (
                <span className="tlm-inline-option-label">可複選</span>
              ) : null}
            </legend>
            <div className={isSingle ? "tlm-inline-options" : "tlm-inline-addons"}>
            {group.options?.map((option) => {
              const checked =
                selectedData?.options?.[group.name]?.[option.name] || false;
              return (
                <label
                  className={`${isSingle ? "tlm-inline-option" : "tlm-inline-addon"} ${
                    checked ? "is-on" : ""
                  }`}
                  key={option.name}
                >
                  <input
                    type={isSingle ? "radio" : "checkbox"}
                    name={`${product.id}-${group.name}`}
                    checked={checked}
                    disabled={!option.enabled}
                    onChange={(event) =>
                      onOptionChange(
                        product.id,
                        group.name,
                        option.name,
                        isSingle ? true : event.target.checked,
                        isSingle
                      )
                    }
                  />
                  {isSingle ? null : <span className="tlm-inline-check">{checked ? "✓" : ""}</span>}
                  <span className="tlm-inline-option-text">{option.name}</span>
                  {Number(option.price || 0) > 0 ? (
                    <span className="tlm-inline-delta">{formatPrice(option.price)}</span>
                  ) : null}
                  {!option.enabled ? <span className="tlm-inline-delta">已售完</span> : null}
                </label>
              );
            })}
            </div>
          </fieldset>
        );
      })}
      <div className="tlm-inline-row">
        <div className="tlm-inline-qty">
          <button type="button" onClick={() => onQuantityChange(product.id, -1)} aria-label="減少數量">
            −
          </button>
          <span>{quantity}</span>
          <button type="button" onClick={() => onQuantityChange(product.id, 1)} aria-label="增加數量">
            +
          </button>
        </div>
        <button className="tlm-inline-cancel" type="button" onClick={onCancel}>
          取消
        </button>
        <button
          className="tlm-inline-add"
          type="button"
          onClick={() => onAdd(product)}
          disabled={!canAdd(product)}
        >
          {isEditing ? "更新" : "加入"} · {formatPrice(calculateSubtotal(product, quantity))}
        </button>
      </div>
    </div>
  );
}

function ProductRow({
  product,
  expanded,
  selectedData,
  isEditing,
  onToggle,
  onCancel,
  onOptionChange,
  onQuantityChange,
  onAdd,
  calculateSubtotal,
  canAdd,
}) {
  const hasOptions = product.groups?.some((group) => group.options?.length);

  return (
    <>
      <button
        className={`tlm-list-item ${product.enabled ? "" : "disabled"} ${
          expanded ? "is-open" : ""
        }`}
        type="button"
        onClick={() => product.enabled && onToggle(product)}
        disabled={!product.enabled}
      >
        <span className="tlm-image-wrap">
          <ProductImage product={product} />
          {!product.enabled ? <span className="tlm-sold-out">完售</span> : null}
        </span>
        <span className="tlm-item-body">
          <span className="tlm-item-row">
            <span className="tlm-item-name">{product.name}</span>
            <span className="tlm-item-price">{formatPrice(product.price)}</span>
          </span>
          <span className="tlm-item-desc">{product.description || " "}</span>
          {hasOptions ? (
            <span className="tlm-meta">
              <span className="tlm-badge">可客製</span>
            </span>
          ) : null}
        </span>
        <span className="tlm-add" aria-hidden="true">
          {expanded ? "−" : "+"}
        </span>
      </button>
      {expanded ? (
        <InlineProductDetail
          product={product}
          selectedData={selectedData}
          isEditing={isEditing}
          onCancel={onCancel}
          onOptionChange={onOptionChange}
          onQuantityChange={onQuantityChange}
          onAdd={onAdd}
          calculateSubtotal={calculateSubtotal}
          canAdd={canAdd}
        />
      ) : null}
    </>
  );
}

function CartBar({ count, total, openCart }) {
  return (
    <button className="tlm-cart-bar" type="button" onClick={openCart} disabled={count === 0}>
      <span className="tlm-cart-left">
        <span className="tlm-cart-qty">{count}</span>
        <span>已點 · {formatPrice(total)}</span>
      </span>
      <span className="tlm-cart-cta">查看訂單</span>
    </button>
  );
}

function TemplateToast({ toast }) {
  if (!toast) return null;

  return (
    <div className="tlm-toast" key={toast.id}>
      <div className="tlm-toast-check">✓</div>
      <div className="tlm-toast-body">
        <div className="tlm-toast-title">已加入訂單</div>
        <div className="tlm-toast-sub">
          {toast.name} × {toast.quantity}
        </div>
      </div>
    </div>
  );
}

const getCartItemKey = (item) => `${item.id}-${JSON.stringify(item.groups)}`;

function CartScreen({
  cart,
  total,
  products,
  editingItemKey,
  currentSelectedData,
  onClose,
  onNext,
  onRemove,
  onEdit,
  onCancelEdit,
  onOptionChange,
  onQuantityChange,
  onSaveEdit,
  calculateSubtotal,
  canAdd,
}) {
  return (
    <div className="tlm-sheet">
      <div className="tlm-sheet-head">
        <button className="tlm-sheet-back" type="button" onClick={onClose} aria-label="返回菜單">
          ←
        </button>
        <div className="tlm-sheet-title-text">你的訂單</div>
        <div className="tlm-sheet-spacer" />
      </div>
      <div className="tlm-sheet-body">
        {cart.length === 0 ? <div className="tlm-empty">還沒有加入任何品項</div> : null}
        {cart.map((item) => {
          const itemKey = getCartItemKey(item);
          const product = products.find((candidate) => String(candidate.id) === String(item.id));
          const isEditing = editingItemKey === itemKey && product;
          return (
            <div className="tlm-cart-block" key={itemKey}>
              <div className={`tlm-cart-row ${isEditing ? "is-open" : ""}`}>
                <div className="tlm-cart-qty-text">{item.quantity}×</div>
                <div className="tlm-cart-main">
                  <div className="tlm-cart-name">{item.name}</div>
                  {item.groups?.some((group) => group.options.length) ? (
                    <div className="tlm-cart-options">
                      {item.groups
                        .filter((group) => group.options.length)
                        .map((group) => (
                          <span key={group.name}>
                            {group.name}: {group.options.map((option) => option.name).join(", ")}
                          </span>
                        ))}
                    </div>
                  ) : null}
                </div>
                <div className="tlm-cart-price">{formatPrice(item.subtotal)}</div>
                <div className="tlm-cart-actions">
                  <button
                    className="tlm-cart-edit"
                    type="button"
                    onClick={() => (isEditing ? onCancelEdit() : onEdit(item))}
                    aria-label={`編輯 ${item.name} 加購選項`}
                  >
                    {isEditing ? "收起" : "編輯"}
                  </button>
                  <button
                    className="tlm-cart-remove"
                    type="button"
                    onClick={() => onRemove(item.id, item.groups)}
                    aria-label={`移除 ${item.name}`}
                  >
                    ×
                  </button>
                </div>
              </div>
              {isEditing ? (
                <InlineProductDetail
                  product={product}
                  selectedData={currentSelectedData[product.id]}
                  isEditing
                  onCancel={onCancelEdit}
                  onOptionChange={onOptionChange}
                  onQuantityChange={onQuantityChange}
                  onAdd={onSaveEdit}
                  calculateSubtotal={calculateSubtotal}
                  canAdd={canAdd}
                />
              ) : null}
            </div>
          );
        })}
        {cart.length > 0 ? (
          <>
            <div className="tlm-cart-rule" />
            <div className="tlm-cart-row total">
              <div className="tlm-cart-qty-text" />
              <div className="tlm-cart-name">合計</div>
              <div className="tlm-cart-price">{formatPrice(total)}</div>
              <div className="tlm-cart-remove-spacer" />
            </div>

          </>
        ) : null}
      </div>
      <div className="tlm-sheet-foot">
        <button
          className="tlm-foot-btn primary"
          type="button"
          disabled={cart.length === 0}
          onClick={onNext}
        >
          下一步 · 產生條碼
        </button>
      </div>
    </div>
  );
}

function QRScreen({ orderString, tableNo, total, onClose }) {
  const orderLabel = tableNo === "takeout" ? "外帶" : tableNo ? `${tableNo} 號桌` : "菜單";

  return (
    <div className="tlm-sheet">
      <div className="tlm-sheet-head">
        <button className="tlm-sheet-back" type="button" onClick={onClose} aria-label="返回菜單">
          ←
        </button>
        <div className="tlm-sheet-title-text">點餐條碼</div>
        <div className="tlm-sheet-spacer" />
      </div>
      <div className="tlm-sheet-body tlm-qr-body">
        <div className="tlm-qr-tag">{orderLabel} · PHONE ORDER</div>
        <div className="tlm-qr-frame">
          <QRCodeSVG value={orderString} size={180} />
        </div>
        <div className="tlm-qr-amount">{formatPrice(total)}</div>
        <div className="tlm-qr-hint">請直接於點餐機掃描付款</div>
        <div className="tlm-qr-row">
          <span className="tlm-qr-step">01 掃碼</span>
          <span className="tlm-qr-step">02 確認</span>
          <span className="tlm-qr-step">03 付款</span>
        </div>
      </div>
      <div className="tlm-sheet-foot">
        <button className="tlm-foot-btn primary" type="button" onClick={onClose}>
          完成
        </button>
      </div>
    </div>
  );
}

export default function TemplateListMenu({
  store,
  storeId,
  tableNo,
  categories,
  products,
  cartData,
  setCartData,
  calculateTotal,
  onAddedToCart,
  invoiceSlot,
}) {
  const [activeCategory, setActiveCategory] = useState("");
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [currentSelectedData, setCurrentSelectedData] = useState({});
  const [editingItemKey, setEditingItemKey] = useState(null);
  const [cartEditingItemKey, setCartEditingItemKey] = useState(null);
  const [view, setView] = useState("menu");
  const [toast, setToast] = useState(null);
  const scrollRef = useRef(null);
  const toastTimerRef = useRef(null);

  const productsByCategory = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => map.set(category.id, []));
    products.forEach((product) => {
      if (!map.has(product.category_id)) {
        map.set(product.category_id, []);
      }
      map.get(product.category_id).push(product);
    });
    return map;
  }, [categories, products]);

  useEffect(() => {
    setActiveCategory(categories[0]?.id || "");
  }, [categories]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || !categories.length) return undefined;

    const onScroll = () => {
      const sections = categories.map((category) => {
        const node = scrollEl.querySelector(`[data-section="${category.id}"]`);
        return {
          id: category.id,
          top: node ? node.offsetTop - scrollEl.scrollTop : Number.POSITIVE_INFINITY,
        };
      });
      const visible = sections.filter((section) => section.top <= 80);
      setActiveCategory(visible.length ? visible[visible.length - 1].id : sections[0].id);
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [categories]);

  const pickCategory = (id) => {
    const scrollEl = scrollRef.current;
    const target = scrollEl?.querySelector(`[data-section="${id}"]`);
    if (scrollEl && target) {
      scrollEl.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    }
    setActiveCategory(id);
  };

  const handleOptionChange = (productId, groupName, optionName, isChecked, isSingle = false) => {
    setCurrentSelectedData((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        options: {
          ...prev[productId]?.options,
          [groupName]: isSingle
            ? { [optionName]: true }
            : {
                ...(prev[productId]?.options?.[groupName] || {}),
                [optionName]: isChecked,
              },
        },
        quantity: prev[productId]?.quantity || 1,
      },
    }));
  };

  const handleQuantityChange = (productId, increment) => {
    setCurrentSelectedData((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: Math.max(1, (prev[productId]?.quantity || 1) + increment),
      },
    }));
  };

  const calculateSubtotal = (product, quantity = 1) => {
    let subtotal = Number(product.price || 0) * quantity;
    if (currentSelectedData[product.id]?.options) {
      product.groups?.forEach((group) => {
        group.options?.forEach((option) => {
          if (currentSelectedData[product.id].options[group.name]?.[option.name]) {
            subtotal += Number(option.price || 0) * quantity;
          }
        });
      });
    }
    return subtotal;
  };

  const areRequiredOptionsSelected = (product) => {
    if (!product.groups?.length) return true;
    return product.groups.every((group) => {
      if (group.min_choose > 0) {
        const selected = currentSelectedData[product.id]?.options?.[group.name] || {};
        return Object.values(selected).filter(Boolean).length >= group.min_choose;
      }
      return true;
    });
  };

  const closeExpandedProduct = () => {
    if (expandedProductId) {
      setCurrentSelectedData((prev) => ({ ...prev, [expandedProductId]: undefined }));
    }
    setEditingItemKey(null);
    setExpandedProductId(null);
  };

  const toggleProductDetail = (product) => {
    if (expandedProductId === product.id) {
      closeExpandedProduct();
      return;
    }
    setEditingItemKey(null);
    setCurrentSelectedData((prev) => ({ ...prev, [product.id]: undefined }));
    setExpandedProductId(product.id);
  };

  const editCartItem = (item) => {
    const product = products.find((candidate) => String(candidate.id) === String(item.id));
    if (!product) return;

    const options = {};
    item.groups?.forEach((group) => {
      options[group.name] = {};
      group.options?.forEach((option) => {
        options[group.name][option.name] = true;
      });
    });

    setCurrentSelectedData((prev) => ({
      ...prev,
      [product.id]: {
        options,
        quantity: item.quantity,
      },
    }));
    setCartEditingItemKey((prev) =>
      prev === getCartItemKey(item) ? null : getCartItemKey(item)
    );
  };

  const upsertCartProduct = (product, itemKeyToReplace = null) => {
    const selectedOptions = currentSelectedData[product.id]?.options || {};
    const quantity = currentSelectedData[product.id]?.quantity || 1;

    const groups =
      product.groups?.map((group) => ({
        name: group.name,
        options:
          group.options
            ?.filter((option) => selectedOptions[group.name]?.[option.name])
            .map((option) => ({ name: option.name, price: option.price })) || [],
      })) || [];

    const optionTotal = groups
      .flatMap((group) => group.options)
      .reduce((sum, option) => sum + Number(option.price || 0), 0);
    const subtotal = Number(product.price || 0) * quantity + optionTotal * quantity;

    setCartData((prev) => {
      const cartWithoutEditedItem = itemKeyToReplace
        ? prev.cart.filter((item) => getCartItemKey(item) !== itemKeyToReplace)
        : prev.cart;
      const existingIndex = cartWithoutEditedItem.findIndex(
        (item) =>
          item.id === product.id &&
          JSON.stringify(item.groups) === JSON.stringify(groups)
      );
      if (existingIndex >= 0) {
        const updatedCart = [...cartWithoutEditedItem];
        updatedCart[existingIndex] = {
          ...updatedCart[existingIndex],
          quantity: updatedCart[existingIndex].quantity + quantity,
          subtotal: updatedCart[existingIndex].subtotal + subtotal,
        };
        return { ...prev, cart: updatedCart };
      }

      return {
        ...prev,
        cart: [
          ...cartWithoutEditedItem,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            quantity,
            groups,
            subtotal,
          },
        ],
      };
    });

    setCurrentSelectedData((prev) => ({ ...prev, [product.id]: undefined }));
    setToast({
      id: Date.now(),
      name: product.name,
      quantity,
    });
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
    onAddedToCart?.();
  };

  const addToCart = (product) => {
    upsertCartProduct(product, editingItemKey);
    setEditingItemKey(null);
    setExpandedProductId(null);
  };

  const saveCartEdit = (product) => {
    upsertCartProduct(product, cartEditingItemKey);
    setCartEditingItemKey(null);
  };

  const cartCount = cartData.cart.reduce((sum, item) => sum + item.quantity, 0);

  const removeFromCart = (productId, groups) => {
    setCartData((prev) => ({
      ...prev,
      cart: prev.cart.filter(
        (item) =>
          item.id !== productId ||
          JSON.stringify(item.groups) !== JSON.stringify(groups)
      ),
    }));
  };

  const generateOrderString = () => {
    const cartString = cartData.cart
      .map((item) => {
        const itemStr = `${item.id},${item.quantity}`;
        const groupsStr = item.groups
          .map((group) => {
            const optionsStr = group.options.map((option) => option.name).join(",");
            return `${group.name},${optionsStr}`;
          })
          .join(",");
        return groupsStr ? `${itemStr},${groupsStr}` : itemStr;
      })
      .join("^^");

    const additionalData = [];
    if (cartData.tableNo !== undefined) {
      if (cartData.tableNo === "takeout") {
        additionalData.push("takeout");
      } else if (cartData.tableNo) {
        additionalData.push(`tableno${cartData.tableNo}`);
      }
    }
    if (cartData.invoiceCarrier) additionalData.push(`mobilecode${cartData.invoiceCarrier}`);
    if (cartData.businessNo) additionalData.push(`vat${cartData.businessNo}`);

    return `phoneorder^^${cartString}${
      additionalData.length ? `^^${additionalData.join("^^")}` : ""
    }`;
  };

  useEffect(
    () => () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    },
    []
  );

  return (
    <div className="tlm-shell">
      <main className="tlm-scroll" ref={scrollRef}>
        <TopBar
          store={store}
          tableNo={tableNo}
          cartCount={cartCount}
          openCart={() => cartCount > 0 && setView("cart")}
        />
        <StoreHero storeId={storeId} store={store} />
        {invoiceSlot ? <div className="tlm-inline-form">{invoiceSlot}</div> : null}

        {categories.map((category) => {
          const categoryProducts = productsByCategory.get(category.id) || [];
          if (!categoryProducts.length) return null;
          return (
            <section className="tlm-section" key={category.id} data-section={category.id}>
              <SectionTitle title={category.label} count={categoryProducts.length} />
              {categoryProducts.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  expanded={expandedProductId === product.id}
                  selectedData={currentSelectedData[product.id]}
                  isEditing={Boolean(editingItemKey && expandedProductId === product.id)}
                  onToggle={toggleProductDetail}
                  onCancel={closeExpandedProduct}
                  onOptionChange={handleOptionChange}
                  onQuantityChange={handleQuantityChange}
                  onAdd={addToCart}
                  calculateSubtotal={calculateSubtotal}
                  canAdd={areRequiredOptionsSelected}
                />
              ))}
            </section>
          );
        })}
      </main>

      <CategoryRail categories={categories} active={activeCategory} onPick={pickCategory} />
      <CartBar count={cartCount} total={calculateTotal()} openCart={() => cartCount > 0 && setView("cart")} />
      <TemplateToast toast={toast} />
      {view === "cart" ? (
        <CartScreen
          cart={cartData.cart}
          total={calculateTotal()}
          products={products}
          editingItemKey={cartEditingItemKey}
          currentSelectedData={currentSelectedData}
          onClose={() => setView("menu")}
          onNext={() => setView("qr")}
          onRemove={removeFromCart}
          onEdit={editCartItem}
          onCancelEdit={() => setCartEditingItemKey(null)}
          onOptionChange={handleOptionChange}
          onQuantityChange={handleQuantityChange}
          onSaveEdit={saveCartEdit}
          calculateSubtotal={calculateSubtotal}
          canAdd={areRequiredOptionsSelected}
        />
      ) : null}
      {view === "qr" ? (
        <QRScreen
          orderString={generateOrderString()}
          tableNo={cartData.tableNo}
          total={calculateTotal()}
          onClose={() => setView("menu")}
        />
      ) : null}
    </div>
  );
}
